/*

? Listing cards inside a chat message (chat audit 2.2, 17 Oct 2026): the listings a message shares
? (its `shared_entity` properties, served as `attached_listings_details`) and the listing an enquiry is
? about (`enquiry_details`), as the apps draw them. The server builds every card only for a listing
? the viewer may read (lib/chat/chatPropertyCard.ts); one it may not, or one that is gone, comes back
? without a title and reads "Listing unavailable". A card opens the listing page in a new tab.

*/

import { HomeWorkOutlined } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { NextRouter } from "next/router";
import React from "react";

const CardList = styled("div")({
	display: "flex",
	flexDirection: "column",
	gap: "6px",
	width: "100%",
	maxWidth: "300px",
});

const Card = styled("a")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "10px",
	padding: "8px",
	borderRadius: "10px",
	border: `1px solid ${theme.palette.divider}`,
	backgroundColor: theme.palette.background.paper,
	color: theme.palette.text.primary,
	textDecoration: "none",
}));

const Cover = styled("img")({
	width: "56px",
	height: "56px",
	borderRadius: "8px",
	objectFit: "cover",
	flexShrink: 0,
});

const Text = styled("div")({
	display: "flex",
	flexDirection: "column",
	minWidth: 0,
});

type ListingCard = {
	property_id?: string;
	title?: string;
	location_title?: string;
	cover_image_url?: string;
	price_label?: string;
	bhk_label?: string;
	slug?: string;
};

const ChatListingCards = ({
	router,
	listings,
	enquiryText,
}: {
	router: NextRouter;
	listings: Array<ListingCard>;
	/* An enquiry's own words, shown under its listing. */
	enquiryText?: string;
}) => {
	const unavailable: string =
		router.locale === "ar-AE" ? "القائمة غير متاحة" : router.locale === "kn-IN" ? "ಪಟ್ಟಿ ಲಭ್ಯವಿಲ್ಲ" : "Listing unavailable";
	const shown = (listings || []).filter((listing) => !!listing);
	if (shown.length === 0) return null;
	return (
		<CardList>
			{shown.map((listing: ListingCard, index: number) => {
				const available: boolean = !!listing.title;
				const href: string | undefined =
					available && listing.slug ? `/${router.locale || "en-IN"}/properties/${listing.slug}` : undefined;
				const meta: string = [listing.bhk_label, listing.price_label].filter((part) => part && part.trim() !== "").join(" • ");
				return (
					<Card
						key={String(listing.property_id || "") || index}
						href={href}
						target={href ? "_blank" : undefined}
						rel={href ? "noopener noreferrer" : undefined}
						sx={{ cursor: href ? "pointer" : "default" }}
					>
						{available && listing.cover_image_url ? (
							<Cover
								src={listing.cover_image_url}
								alt={listing.title}
								referrerPolicy="no-referrer"
							/>
						) : (
							<HomeWorkOutlined sx={{ fontSize: "2rem", color: "text.secondary" }} />
						)}
						<Text>
							<Typography
								variant="body2"
								sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
							>
								{available ? listing.title : unavailable}
							</Typography>
							{available && listing.location_title ? (
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
								>
									{listing.location_title}
								</Typography>
							) : null}
							{available && meta ? (
								<Typography
									variant="caption"
									color="text.secondary"
								>
									{meta}
								</Typography>
							) : null}
						</Text>
					</Card>
				);
			})}
			{enquiryText ? (
				<Typography
					variant="body2"
					sx={{ fontSize: "0.875rem" }}
				>
					{enquiryText}
				</Typography>
			) : null}
		</CardList>
	);
};

export default ChatListingCards;
